import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
    PiAnchorBold,
    PiArrowRightBold,
    PiCalendarBlankBold,
    PiClockBold,
    PiCurrencyDollarBold,
    PiTruckBold,
    PiWarningBold,
    PiCheckCircleBold,
    PiArrowsClockwiseBold,
    PiCaretRightBold,
} from 'react-icons/pi';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

/* ─── tipos ─────────────────────────────────────────────── */
type FlowStep  = { l: string; s: string; count: number };
type Flow      = { name: string; steps: FlowStep[] };
type AlertItem = { type: string; title: string; sub: string; tag: string };
type Vessel    = { id: string; vessel: string; route: string; status: string; pos: string; eta: string };
type Queue = {
    id: number;
    code: string;
    client: string;
    supplier: string;
    detail: string;
    stage: string;
    risk_score: number;
    etaClass?: 'ok' | 'late' | 'changed';
    eta: string;
    eta_raw?: string;
    priority: 'P1' | 'P2' | 'P3';
    risk_reasons: string[];
};
type AgingRow  = { label: string; days: number; max: number; color?: string; count: number };
type DocRow    = { name: string; status: string; icon?: string };
type TimelineItem = { time: string; title: string; meta?: string; color?: string };
type TableRow  = {
    po: string; pi: string; sup: string; cntr: string; arm: string;
    etd: string; eta: string; delay?: number; status: 'ok' | 'delayed' | 'critical';
    stage: string; resp?: string; pri?: number; fin?: string; com: string;
};

type ComexData = {
    kpis: {
        in_transit: number;
        critical_alerts: number;
        eta_changed_week: number;
        delivered_month: number;
        finance_pending?: number;
        total_open?: number;
    };
    stage_counts?: Record<string, number>;
    flows: Flow[];
    alerts: AlertItem[];
    vessels: Vessel[];
    queues: Queue[];
    aging: AgingRow[];
    docs: DocRow[];
    timeline: TimelineItem[];
    tableRows: TableRow[];
    stats: { alertCountLabel: string };
};

/* ─── constantes visuais ──────────────────────────────────── */
const PRIORITY_CLS: Record<string, string> = {
    P1: 'bg-red-600 text-white',
    P2: 'bg-amber-500 text-white',
    P3: 'bg-sky-600 text-white',
};

const STATUS_STEP: Record<string, string> = {
    active:   'bg-sky-500 text-white',
    warn:     'bg-amber-500 text-white',
    critical: 'bg-red-600 text-white',
    ok:       'bg-emerald-500 text-white',
    pending:  'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

const STATUS_BADGE: Record<string, string> = {
    pending:  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    active:   'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
    ok:       'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    warn:     'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const ETA_CLS: Record<string, string> = {
    late:    'text-red-600 dark:text-red-400 font-semibold',
    changed: 'text-amber-600 dark:text-amber-400 font-semibold',
    ok:      'text-emerald-600 dark:text-emerald-400',
};

/* ─── sub-componentes ─────────────────────────────────────── */

function KpiCard({
    label, value, icon: Icon, alert, sub, to,
}: {
    label: string; value: string | number; icon: React.ComponentType<{className?: string}>;
    alert?: boolean; sub?: string; to?: string;
}) {
    const body = (
        <div className={cn(
            'flex items-start gap-4 rounded-2xl border p-5 transition',
            alert
                ? 'border-red-200 bg-red-50 dark:border-red-800/60 dark:bg-red-950/30'
                : 'border-gray-200 bg-white dark:border-gray-700/60 dark:bg-gray-800/80',
            to && 'hover:shadow-md',
        )}>
            <span className={cn(
                'flex size-12 shrink-0 items-center justify-center rounded-xl',
                alert
                    ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
            )}>
                <Icon className="size-6" />
            </span>
            <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
                <p className={cn(
                    'mt-1 text-3xl font-bold tabular-nums',
                    alert ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100',
                )}>{value}</p>
                {sub && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</p>}
            </div>
        </div>
    );
    return to ? <Link to={to} className="contents">{body}</Link> : body;
}

function DashCard({
    title, subtitle, badge, action, children, className,
}: {
    title: string; subtitle?: string; badge?: React.ReactNode; action?: React.ReactNode;
    children: React.ReactNode; className?: string;
}) {
    return (
        <section className={cn(
            'flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm',
            'dark:border-gray-700/60 dark:bg-gray-800/80',
            className,
        )}>
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700/40">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                    {subtitle && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {badge}
                    {action}
                </div>
            </div>
            <div className="flex-1 px-5 py-4">{children}</div>
        </section>
    );
}

/** Pipeline horizontal com contagem por estágio */
function PipelineViz({ flows }: { flows: Flow[] }) {
    if (!flows.length) return <p className="text-sm text-gray-400">Sem dados.</p>;
    const steps = flows[0].steps;
    return (
        <div className="flex flex-wrap items-center gap-y-3">
            {steps.map((step, i) => (
                <span key={step.l} className="flex items-center">
                    <span className={cn(
                        'flex flex-col items-center gap-0.5 rounded-xl px-3 py-2 text-center text-xs font-semibold',
                        step.count > 0 ? STATUS_STEP[step.s] ?? STATUS_STEP.active : STATUS_STEP.pending,
                    )}>
                        <span className="text-lg font-bold leading-none">{step.count}</span>
                        <span className="opacity-90">{step.l}</span>
                    </span>
                    {i < steps.length - 1 && (
                        <PiCaretRightBold className="mx-1.5 size-4 shrink-0 text-gray-300 dark:text-gray-600" />
                    )}
                </span>
            ))}
        </div>
    );
}

/** Lista de alertas críticos */
function AlertList({ alerts }: { alerts: AlertItem[] }) {
    if (!alerts.length) return (
        <p className="flex items-center gap-2 py-4 text-sm text-emerald-600 dark:text-emerald-400">
            <PiCheckCircleBold className="size-5" /> Sem alertas de risco no momento.
        </p>
    );
    return (
        <ul className="divide-y divide-gray-50 dark:divide-gray-700/30">
            {alerts.map((a, i) => {
                const isCritical = a.type === 'critical';
                return (
                    <li key={i} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{a.title}</p>
                            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{a.sub}</p>
                        </div>
                        <span className={cn(
                            'shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold',
                            isCritical
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                        )}>
                            {a.tag}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}

/** Tabela de próximos containers */
function QueuesTable({ queues }: { queues: Queue[] }) {
    if (!queues.length) return <p className="py-4 text-sm text-gray-400">Nada na fila.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700/40">
                        {['Processo', 'Cliente', 'Fornecedor', 'Estágio', 'ETA', 'Score', 'Risco'].map(h => (
                            <th key={h} className="pb-2 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 last:pr-0">
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/30">
                    {queues.map(q => (
                        <tr key={q.code} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/20">
                            <td className="py-2.5 pr-4">
                                <Link
                                    to={`/shipments/${q.id}`}
                                    className="font-semibold text-primary hover:underline"
                                >
                                    {q.code}
                                </Link>
                            </td>
                            <td className="max-w-[9rem] truncate py-2.5 pr-4 text-gray-700 dark:text-gray-300" title={q.client}>
                                {q.client}
                            </td>
                            <td className="max-w-[7rem] truncate py-2.5 pr-4 text-xs text-gray-500 dark:text-gray-400" title={q.supplier}>
                                {q.supplier}
                            </td>
                            <td className="py-2.5 pr-4 text-xs text-gray-600 dark:text-gray-400">{q.stage}</td>
                            <td className={cn('whitespace-nowrap py-2.5 pr-4 tabular-nums', ETA_CLS[q.etaClass ?? 'ok'])}>
                                {q.eta}
                            </td>
                            <td className="py-2.5 pr-4">
                                <div className="flex items-center gap-1.5">
                                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                        <div
                                            className={cn('h-full rounded-full', q.risk_score >= 70 ? 'bg-red-500' : q.risk_score >= 40 ? 'bg-amber-400' : 'bg-emerald-500')}
                                            style={{ width: `${Math.min(100, q.risk_score)}%` }}
                                        />
                                    </div>
                                    <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{q.risk_score}</span>
                                </div>
                            </td>
                            <td className="py-2.5">
                                <span className={cn(
                                    'rounded-full px-2.5 py-0.5 text-[11px] font-bold',
                                    PRIORITY_CLS[q.priority] ?? PRIORITY_CLS.P3,
                                )}>
                                    {q.priority}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Aging por estágio */
function AgingList({ aging }: { aging: AgingRow[] }) {
    if (!aging.length) return <p className="py-4 text-sm text-gray-400">Sem dados.</p>;
    return (
        <ul className="space-y-3">
            {aging.map(a => {
                const pct = a.max > 0 ? Math.min(100, (a.days / a.max) * 100) : 0;
                const isHot = a.days > 20;
                return (
                    <li key={a.label}>
                        <div className="mb-1 flex items-center justify-between text-xs">
                            <span className="font-semibold text-gray-700 dark:text-gray-200">{a.label}</span>
                            <span className={cn('tabular-nums', isHot ? 'font-bold text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400')}>
                                {a.days}d · {a.count} proc.
                            </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                            <div
                                className="h-full rounded-full transition-all"
                                style={{ width: `${pct}%`, backgroundColor: isHot ? '#f59e0b' : (a.color ?? '#3b82f6') }}
                            />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

/** Timeline de eventos */
function TimelineList({ timeline }: { timeline: TimelineItem[] }) {
    if (!timeline.length) return <p className="py-4 text-sm text-gray-400">Sem eventos recentes.</p>;
    return (
        <ol className="relative space-y-4 border-l border-gray-200 pl-4 dark:border-gray-700/50">
            {timeline.map((t, i) => (
                <li key={i} className="relative">
                    <span
                        className="absolute -left-[1.32rem] top-1 size-3 rounded-full"
                        style={{ backgroundColor: t.color ?? '#3b82f6' }}
                    />
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">{t.time}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t.title}</p>
                    {t.meta && <p className="truncate text-xs text-gray-500 dark:text-gray-400">{t.meta}</p>}
                </li>
            ))}
        </ol>
    );
}

/* ─── PÁGINA ─────────────────────────────────────────────── */
export default function ComexDashboardPage() {
    const { data, isLoading, isError, refetch, isFetching } = useQuery<ComexData>({
        queryKey: ['dashboard', 'comex'],
        queryFn: async () => (await api.get('/dashboard/comex')).data,
        refetchInterval: 5 * 60 * 1000,
    });

    if (isLoading) return <LoadingState label="Carregando dashboard COMEX…" />;
    if (isError || !data) return <p className="text-sm text-red-600">Não foi possível carregar o dashboard COMEX.</p>;

    const k = data.kpis;
    const hasCritical = k.critical_alerts > 0;

    return (
        <div className="space-y-6">

            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard COMEX</h1>
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                        {k.total_open ?? '—'} processos abertos · {data.stats.alertCountLabel}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50"
                    >
                        <PiArrowsClockwiseBold className={cn('size-4', isFetching && 'animate-spin')} />
                        Atualizar
                    </button>
                    <Link
                        to="/shipments"
                        className="flex items-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200"
                    >
                        Ver embarques <PiArrowRightBold className="size-4" />
                    </Link>
                </div>
            </div>

            {/* ── Banner de alerta crítico ────────────────────────────── */}
            {hasCritical && (
                <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 dark:border-red-800/50 dark:bg-red-950/30">
                    <PiWarningBold className="size-6 shrink-0 text-red-600 dark:text-red-400" />
                    <div className="min-w-0 flex-1">
                        <p className="font-semibold text-red-800 dark:text-red-300">
                            {k.critical_alerts} processo{k.critical_alerts > 1 ? 's' : ''} com risco crítico de atraso
                        </p>
                        <p className="mt-0.5 text-sm text-red-600 dark:text-red-400">
                            Ação imediata necessária — verifique os alertas abaixo.
                        </p>
                    </div>
                    <Link to="/shipments?risk=high" className="shrink-0 text-sm font-semibold text-red-700 hover:underline dark:text-red-300">
                        Ver todos <PiArrowRightBold className="inline size-3.5" />
                    </Link>
                </div>
            )}

            {/* ── KPI strip ──────────────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                <KpiCard label="Processos abertos"   value={k.total_open ?? '—'}       icon={PiAnchorBold}        to="/shipments" />
                <KpiCard label="Em trânsito"          value={k.in_transit}               icon={PiTruckBold} />
                <KpiCard label="Risco crítico"        value={k.critical_alerts}          icon={PiWarningBold}       alert={hasCritical} sub="precisam de ação" />
                <KpiCard label="ETA alterado (7d)"   value={k.eta_changed_week}         icon={PiCalendarBlankBold} sub="últimos 7 dias" alert={k.eta_changed_week > 0} />
                <KpiCard label="Fin. pendente"        value={k.finance_pending ?? '—'}   icon={PiCurrencyDollarBold} alert={(k.finance_pending ?? 0) > 0} sub="aguardando pag." />
                <KpiCard label="Entregues no mês"    value={k.delivered_month}          icon={PiCheckCircleBold}   sub="finalizados" />
            </div>

            {/* ── Pipeline + Alertas ─────────────────────────────────── */}
            <div className="grid gap-5 xl:grid-cols-3">
                <DashCard
                    title="Pipeline de importação"
                    subtitle="Processos abertos por estágio"
                    className="xl:col-span-2"
                    action={
                        <Link to="/shipments" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                            Ver tudo <PiArrowRightBold className="size-3" />
                        </Link>
                    }
                >
                    <PipelineViz flows={data.flows} />
                </DashCard>

                <DashCard
                    title="Alertas de risco"
                    subtitle="Processos críticos e de atenção"
                    badge={
                        hasCritical ? (
                            <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                {k.critical_alerts} críticos
                            </span>
                        ) : null
                    }
                >
                    <AlertList alerts={data.alerts} />
                </DashCard>
            </div>

            {/* ── Próximos containers (tabela central) ───────────────── */}
            <DashCard
                title="Próximos containers — Radar de risco"
                subtitle="Ordenados por score de atraso (maiores primeiro) · Clique no processo para abrir"
                badge={
                    <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                        Top {data.queues.length}
                    </span>
                }
            >
                <QueuesTable queues={data.queues} />
            </DashCard>

            {/* ── Aging + Timeline ───────────────────────────────────── */}
            <div className="grid gap-5 lg:grid-cols-2">
                <DashCard
                    title="Aging por estágio"
                    subtitle="Tempo médio em cada etapa (cap 30 dias) · Laranja = acima de 20 dias"
                >
                    <AgingList aging={data.aging} />
                </DashCard>

                <DashCard
                    title="Atividade recente"
                    subtitle="Últimos avanços registrados nos processos"
                >
                    <TimelineList timeline={data.timeline} />
                </DashCard>
            </div>

            {/* ── Tabela operacional completa ────────────────────────── */}
            <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-800/80">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700/40">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Processos abertos — Visão operacional</h3>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Top 80 · Vermelho = risco alto · Amarelo = atrasado</p>
                    </div>
                    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {data.tableRows.length} linhas
                    </span>
                </div>

                {data.tableRows.length === 0 ? (
                    <p className="px-5 py-8 text-center text-sm text-gray-400">Sem processos abertos.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100 text-sm dark:divide-gray-700/30">
                            <thead>
                                <tr className="bg-gray-50/80 dark:bg-gray-800/60">
                                    {['Processo','PI','Fornecedor','Container','Armador','ETD','ETA','Δ dias','Estágio','Risco','Pri','Frete'].map(h => (
                                        <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/20">
                                {data.tableRows.map((row) => {
                                    const isCrit = row.status === 'critical';
                                    const isDelay = row.status === 'delayed';
                                    return (
                                        <tr
                                            key={row.po}
                                            className={cn(
                                                'transition',
                                                isCrit  ? 'bg-red-50/60 dark:bg-red-950/20 hover:bg-red-50 dark:hover:bg-red-950/30'
                                                : isDelay ? 'bg-amber-50/40 dark:bg-amber-950/10 hover:bg-amber-50/60 dark:hover:bg-amber-950/20'
                                                : 'hover:bg-gray-50/60 dark:hover:bg-gray-700/20',
                                            )}
                                        >
                                            <td className="whitespace-nowrap px-3 py-2.5">
                                                <span className="font-semibold text-gray-900 dark:text-gray-100">{row.po}</span>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2.5 text-gray-600 dark:text-gray-400">{row.pi}</td>
                                            <td className="max-w-[9rem] truncate px-3 py-2.5 text-gray-700 dark:text-gray-300" title={row.sup}>{row.sup}</td>
                                            <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-gray-600 dark:text-gray-400">{row.cntr}</td>
                                            <td className="max-w-[8rem] truncate px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400" title={row.arm}>{row.arm}</td>
                                            <td className="whitespace-nowrap px-3 py-2.5 text-center tabular-nums text-gray-600 dark:text-gray-400">{row.etd}</td>
                                            <td className="whitespace-nowrap px-3 py-2.5 text-center tabular-nums text-gray-600 dark:text-gray-400">{row.eta}</td>
                                            <td className={cn(
                                                'whitespace-nowrap px-3 py-2.5 text-center tabular-nums font-semibold',
                                                (row.delay ?? 0) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400',
                                            )}>
                                                {row.delay ?? 0}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400">{row.stage}</td>
                                            <td className="whitespace-nowrap px-3 py-2.5">
                                                <span className={cn(
                                                    'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                                                    isCrit ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                    : isDelay ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                                                )}>
                                                    {row.com}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2.5 text-center">
                                                <span className={cn(
                                                    'rounded-full px-2 py-0.5 text-[11px] font-bold',
                                                    PRIORITY_CLS[`P${row.pri ?? 3}`] ?? PRIORITY_CLS.P3,
                                                )}>
                                                    P{row.pri ?? 3}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">{row.fin ?? '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
